/**
 * Integration with the whitelabel mobile app (React Native WebView wrapper).
 *
 * Whether a host serves the app is a property of the tenant
 * (`apiPartnerCode === 'APPLICATION'`), and which app it is comes from the
 * tenant's `settings.platforms` (package name / bundle id — also used as the
 * app's custom URL scheme). No static, shared scheme exists, so deep links
 * work with any number of whitelabel apps installed side by side.
 *
 * The app additionally appends `OrderMageApp/<applicationId>` to the WebView
 * User-Agent; that marker is only used to detect "we are already rendering
 * inside the app's WebView".
 */

export const MOBILE_APP_UA_MARKER = 'OrderMageApp/';

export function isMobileAppWebView(userAgent: string | null | undefined): boolean {
    return Boolean(userAgent?.includes(MOBILE_APP_UA_MARKER));
}

/**
 * Deep link that reopens the whitelabel app on the given web URL. Only the
 * path + query travel through the link — the app maps them onto its own
 * configured web origin.
 */
export function buildAppDeepLink(
    appId: string,
    target: Pick<URL, 'pathname' | 'search' | 'href'>,
    platform: 'android' | 'ios',
): string {
    const pathAndQuery = `${target.pathname}${target.search}`.replace(/^\/+/, '');

    if (platform === 'android') {
        // intent:// pins the target package, so the correct whitelabel app opens
        // even with several installed; the browser stays on the web URL otherwise.
        return `intent://${pathAndQuery}#Intent;scheme=${appId};package=${appId};S.browser_fallback_url=${encodeURIComponent(target.href)};end`;
    }

    return `${appId}://${pathAndQuery}`;
}
